import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useLocation, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { extractDomainWithPath } from '@/utils/urlUtils'
import {
  ArrowLeft,
  BarChart3,
  PieChart,
  History,
  Play,
  PlayCircle,
  CalendarClock,
  Settings,
  ChevronLeft,
  ChevronRight,
  Users,
  FileText,
  Bell,
  Globe,
  Search,
  ShieldCheck,
  Database,
  Crosshair,
  ClipboardList,
  type LucideIcon,
  User as UserIcon,
  Settings as SettingsIcon,
  LogOut as LogOutIcon,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { Badge } from '@/components/common/ui/badge'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/common/ui/tooltip'
import { Button } from '@/components/common/ui/button'
import { useSidebarStore } from '@/stores/sidebar-store'
import { useServiceDetail } from '@/hooks/features/service/useServiceDetail'
import { useFeatureUsage } from '@/hooks/features/prompt/useFeatureUsage'
import { useHallucinationFlags } from '@/hooks/features/hallucination'
import { useSpaceRedirect } from '@/hooks/features/space/useSpaceRedirect'
import type { AnalysisUsageResponse } from '@/types/api/responses'
import { useAuth } from '@/hooks/common/useAuth'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/common/ui/popover'

interface NavItem {
  id: string
  label: string
  icon?: LucideIcon
  path?: string
  children?: NavItem[]
  isNew?: boolean
  isBeta?: boolean
  showDividerBefore?: boolean
  isDisabled?: boolean
  statusLabel?: string
  /** 大セクション見出し（AIO分析 / ハルシネーション検知）。配下のサブグループより目立たせる。 */
  isSection?: boolean
}

interface SidebarNavProps {
  onNavigate?: () => void
  onToggleSidebar?: () => void
}

type UsageSectionId = 'analysis' | 'prompt' | 'hallucination'

type UsageSectionStyle = {
  accentTextClass: string
  badgeTextClass: string
  progressClass: string
}

const SECTION_STYLES: Record<UsageSectionId, UsageSectionStyle> = {
  analysis: {
    accentTextClass: 'text-indigo-600',
    badgeTextClass: 'text-indigo-600',
    progressClass: 'bg-gradient-to-r from-indigo-500 via-indigo-600 to-indigo-700',
  },
  prompt: {
    accentTextClass: 'text-emerald-700',
    badgeTextClass: 'text-emerald-700',
    progressClass: 'bg-gradient-to-r from-emerald-600 via-emerald-700 to-teal-700',
  },
  hallucination: {
    accentTextClass: 'text-violet-600',
    badgeTextClass: 'text-violet-600',
    progressClass: 'bg-gradient-to-r from-violet-500 via-violet-600 to-purple-700',
  },
}

export function SidebarNav({ onNavigate, onToggleSidebar }: SidebarNavProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const params = useParams<{ space_id?: string; service_id?: string }>()
  const { isCollapsed } = useSidebarStore()
  const { user, signOut } = useAuth()
  const { initialSpace } = useSpaceRedirect()

  // Only fetch service detail if we have both IDs
  const { service } = useServiceDetail(params.space_id || '', params.service_id || '')

  const { primaryDomain, faviconDomain } = useMemo(() => {
    // Calculate primaryDomain
    if (!service?.monitoringDomains || service.monitoringDomains.length === 0) {
      return { primaryDomain: '', faviconDomain: '' }
    }

    const preferred = service.monitoringDomains.find(domain => domain.isPrimary && domain.domain)
    const primary = preferred?.domain || service.monitoringDomains[0]?.domain || ''

    // Calculate faviconDomain from primary
    if (!primary) return { primaryDomain: '', faviconDomain: '' }

    const extractedDomain = extractDomainWithPath(primary)
    if (!extractedDomain) {
      console.warn('[SidebarNav] Failed to parse domain for favicon:', primary)
      return { primaryDomain: '', faviconDomain: '' }
    }

    // Get just the hostname part (without path) for favicon
    const hostname = extractedDomain.split('/')[0]

    // Basic validation: ensure it looks like a valid domain
    if (!hostname || !hostname.includes('.') || hostname.length < 4) {
      console.warn('[SidebarNav] Invalid domain format for favicon:', hostname)
      return { primaryDomain: '', faviconDomain: '' }
    }

    // Additional validation: check for suspicious patterns
    if (hostname.includes('<') || hostname.includes('>') || hostname.includes('javascript:')) {
      console.error('[SidebarNav] Potentially malicious domain detected:', hostname)
      return { primaryDomain: '', faviconDomain: '' }
    }

    // Extra validation: try to construct a URL to catch malformed or malicious hostnames
    try {
      // This will throw if hostname is not a valid domain
      // Use a dummy protocol to allow parsing
      const url = new URL(`http://${hostname}`)
      // Optionally, check that the hostname matches the input (no path, etc)
      if (url.hostname !== hostname) {
        console.error('[SidebarNav] Hostname mismatch after URL parsing:', hostname)
        return { primaryDomain: '', faviconDomain: '' }
      }
    } catch (e) {
      console.error('[SidebarNav] Invalid hostname (URL parse failed):', hostname)
      return { primaryDomain: '', faviconDomain: '' }
    }

    return { primaryDomain: primary, faviconDomain: hostname }
  }, [service?.monitoringDomains])

  const [iconSrc, setIconSrc] = useState<string | null>(null)
  const [hasTriedFallback, setHasTriedFallback] = useState(false)

  useEffect(() => {
    if (!faviconDomain) {
      setIconSrc(null)
      setHasTriedFallback(false)
      return
    }

    // Encode the domain to prevent injection attacks
    const encodedDomain = encodeURIComponent(faviconDomain)
    setIconSrc(`https://www.google.com/s2/favicons?sz=96&domain=${encodedDomain}`)
    setHasTriedFallback(false)
  }, [faviconDomain])

  const handleIconError = () => {
    if (!hasTriedFallback && faviconDomain) {
      setHasTriedFallback(true)
      // Encode domain for fallback as well
      const encodedDomain = encodeURIComponent(faviconDomain)
      setIconSrc(`https://icons.duckduckgo.com/ip3/${encodedDomain}.ico`)
      return
    }

    setIconSrc(null)
  }

  // Resolve IDs from params or fallback to parsing the current pathname
  // This helps tests that mock only useLocation without routing params
  const { pathname } = location
  const isSettingsPage = pathname?.startsWith('/settings')
  const pathMatch = /\/space\/([^/]+)\/service\/([^/]+)/.exec(pathname || '')
  const hasAnyParam = Boolean(params.space_id || params.service_id)
  const resolvedSpaceId = hasAnyParam
    ? params.space_id || (pathMatch ? pathMatch[1] : undefined)
    : params.space_id
  const resolvedServiceId = hasAnyParam
    ? params.service_id || (pathMatch ? pathMatch[2] : undefined)
    : params.service_id

  // ハルシネーション検知は Space 単位の feature flag で有効な Space のみサイドバーに出す（N-1）。
  // fail-close: 未確定（ロード中・エラー）は非表示＝BE の requireHallucinationEnabled と片側ガードにしない。
  const { isEnabled: isHallucinationEnabled } = useHallucinationFlags(resolvedSpaceId)

  // Always render nav container; when IDs are missing, links may be inactive

  const settingsNavItems: NavItem[] = [
    {
      id: 'settings-root',
      label: t('settings.title', 'Settings'),
      icon: Settings,
      children: [
        {
          id: 'settings-account',
          label: t('settings.account.title', 'Account'),
          icon: Settings,
          path: `/settings?section=account`,
        },
        {
          id: 'settings-language',
          label: t('settings.language.title', 'Language'),
          icon: Globe,
          path: `/settings?section=language`,
        },
        {
          id: 'settings-notifications',
          label: t('settings.notifications.title'),
          icon: Bell,
          path: `/settings?section=notifications`,
        },
      ],
    },
  ]

  const rawNavItems: NavItem[] = isSettingsPage
    ? settingsNavItems
    : resolvedServiceId
      ? [
          {
            // 大セクション①: AIO分析（配下に アナリティクス / 分析 の2サブグループ）。
            id: 'aio',
            label: t('sidebar.categories.aio', 'AIO分析'),
            icon: BarChart3,
            isSection: true,
            children: [
              {
                id: 'analytics',
                label: t('sidebar.categories.analytics'),
                icon: PieChart,
                children: [
                  {
                    id: 'analysis-summary',
                    label: t('sidebar.items.analysisSummary'),
                    icon: PieChart,
                    path: `/space/${resolvedSpaceId}/service/${resolvedServiceId}/overview/summary`,
                    isBeta: true,
                  },
                  {
                    id: 'execution-history',
                    label: t('sidebar.items.executionHistory'),
                    icon: History,
                    path: `/space/${resolvedSpaceId}/service/${resolvedServiceId}/overview/execution-history`,
                  },
                  {
                    id: 'prompt-analysis',
                    label: t('sidebar.items.promptAnalysis'),
                    icon: Search,
                    path: `/space/${resolvedSpaceId}/service/${resolvedServiceId}/overview/prompt-analysis`,
                  },
                ],
              },
              {
                id: 'analysis',
                label: t('sidebar.categories.analysis'),
                icon: PlayCircle,
                showDividerBefore: true,
                children: [
                  {
                    id: 'manual',
                    label: t('sidebar.items.manualAnalysis'),
                    icon: Play,
                    path: `/space/${resolvedSpaceId}/service/${resolvedServiceId}/analysis/manual`,
                  },
                  {
                    id: 'analysis-status',
                    label: t('sidebar.items.analysisStatus'),
                    icon: FileText,
                    path: `/space/${resolvedSpaceId}/service/${resolvedServiceId}/analysis/status`,
                  },
                  {
                    id: 'scheduled',
                    label: t('sidebar.items.scheduledExecution'),
                    icon: CalendarClock,
                    path: `/space/${resolvedSpaceId}/service/${resolvedServiceId}/analysis/scheduled`,
                    isBeta: true,
                  },
                ],
              },
            ],
          },
          {
            // 大セクション②: ハルシネーション検知（検知実行 S2 / 検知ステータス S3 / 正解データ管理 S1）。
            id: 'hallucination',
            label: t('sidebar.categories.hallucination', 'ハルシネーション検知'),
            icon: ShieldCheck,
            isSection: true,
            showDividerBefore: true,
            children: [
              {
                id: 'hallucination-detection-execution',
                label: t('sidebar.items.hallucinationDetectionExecution', '検知実行'),
                icon: Crosshair,
                path: `/space/${resolvedSpaceId}/service/${resolvedServiceId}/hallucination/detection`,
                isBeta: true,
              },
              {
                id: 'hallucination-detection-status',
                label: t('sidebar.items.hallucinationDetectionStatus', '検知ステータス'),
                icon: ClipboardList,
                path: `/space/${resolvedSpaceId}/service/${resolvedServiceId}/hallucination/results`,
                isBeta: true,
              },
              {
                id: 'hallucination-sources',
                label: t('sidebar.items.hallucinationSources', '正解データ管理'),
                icon: Database,
                path: `/space/${resolvedSpaceId}/service/${resolvedServiceId}/hallucination/sources`,
                isBeta: true,
              },
            ],
          },
          /* {
            id: 'prompts',
            label: t('sidebar.items.promptManagement'),
            icon: Sparkles,
            showDividerBefore: true,
            children: [
              {
                id: 'prompt-manual-input',
                label: t('sidebar.items.promptManualInput'),
                icon: PenSquare,
                isDisabled: true,
                statusLabel: t('sidebar.status.notImplemented', '未実装'),
              },
              {
                id: 'prompt-keyword-generation',
                label: t('sidebar.items.promptKeywordGeneration'),
                icon: Wand2,
                isDisabled: true,
                statusLabel: t('sidebar.status.notImplemented', '未実装'),
              },
              {
                id: 'prompt-monitoring',
                label: t('sidebar.items.promptMonitoring'),
                icon: Eye,
                isDisabled: true,
                statusLabel: t('sidebar.status.notImplemented', '未実装'),
              },
            ],
          }, */
          {
            id: 'settings',
            label: t('sidebar.categories.settings'),
            icon: Settings,
            path: `/space/${resolvedSpaceId}/service/${resolvedServiceId}/settings`,
            showDividerBefore: true,
          },
        ]
      : [
          {
            id: 'space-overview',
            label: t('sidebar.space.overview', 'スペース'),
            icon: BarChart3,
            children: [
              {
                id: 'space-services',
                label: t('sidebar.space.services', 'サービス一覧'),
                icon: Users,
                path: `/space/${resolvedSpaceId}`,
              },
              {
                id: 'space-contract-plan',
                label: t('sidebar.space.contractPlan', '契約プラン'),
                icon: FileText,
                path: `/space/${resolvedSpaceId}/contract-plan`,
              },
            ],
          },
        ]

  // N-1: 機能無効 Space では検知セクションを描画しない（BE ガードと整合・片側ガード解消）。
  const navItems: NavItem[] = rawNavItems.filter(
    item => item.id !== 'hallucination' || isHallucinationEnabled
  )

  const handleNavigate = (path: string) => {
    navigate(path)
    onNavigate?.()
  }

  const handleBackToSpace = () => {
    const sid = resolvedSpaceId || initialSpace || ''
    navigate(`/space/${sid}`)
    onNavigate?.()
  }

  const isActive = (path?: string) => {
    if (!path) return false
    const current = `${location.pathname || ''}${location.search || ''}`
    // Special case: settings page without explicit section should highlight default section (account)
    if (isSettingsPage) {
      const params = new URLSearchParams(location.search || '')
      const section = params.get('section')
      // Default section when none specified
      const defaultSettingsPath = '/settings?section=account'
      if (!section && path.startsWith(defaultSettingsPath)) return true
    }

    // Exact match first
    if (current === path) return true

    // For space root paths, only match if current is exactly that path (not subpaths)
    if (path.match(/\/space\/[^/]+$/)) {
      return current === path
    }

    // For other paths, use startsWith but ensure it's a proper path boundary
    if (current.startsWith(path)) {
      const remainingPath = current.slice(path.length)
      // Only match if the remaining path starts with '/' or '?' (proper path boundary)
      return remainingPath === '' || remainingPath.startsWith('/') || remainingPath.startsWith('?')
    }

    // Heuristic for overview summary when router/mocks differ slightly
    if (current.includes('/overview/summary') && path.includes('/overview/summary')) return true
    return false
  }

  const renderNavItem = (item: NavItem, isChild = false) => {
    const hasChildren = item.children && item.children.length > 0
    const active = isActive(item.path)
    const isDisabled = Boolean(item.isDisabled)
    const statusLabel = item.statusLabel

    if (hasChildren) {
      const groupDivider = (
        <div className="px-2">
          <div className="h-px w-full rounded-full bg-gradient-to-r from-transparent via-indigo-100 to-transparent" />
        </div>
      )

      return (
        <div
          key={item.id}
          className={cn(
            // ネスト時は w-full を外す（w-full + ml-4 = 100%+1rem で右に食み出すため）。
            // 非ネストは従来どおり w-full、ネストは自動幅＋ml-4（マージン分だけ縮む）。
            isCollapsed ? 'w-full' : cn('mt-3 first:mt-0', isChild ? 'ml-4' : 'w-full')
          )}
        >
          {!isCollapsed && item.showDividerBefore && (
            <div className="mx-3 mb-3 h-px rounded-full bg-gradient-to-r from-transparent via-indigo-100 to-transparent" />
          )}
          {isCollapsed ? (
            // 折りたたみ時も罫線は showDividerBefore の境界だけ（展開時と一致・ネスト由来の二重を防ぐ）。
            item.showDividerBefore ? (
              groupDivider
            ) : null
          ) : (
            <div
              className={cn(
                'flex items-center gap-2 px-3 py-2',
                item.isSection
                  ? // 大セクション見出し（AIO分析 / ハルシネーション検知）は目立たせる。
                    'text-sm font-bold text-slate-800'
                  : // サブグループ見出し（アナリティクス / 分析）は控えめに。
                    'text-xs font-semibold uppercase tracking-wide text-muted-foreground'
              )}
            >
              {item.icon && (
                <item.icon
                  className={cn(
                    'shrink-0',
                    item.isSection ? 'h-4 w-4 text-indigo-600' : 'h-3.5 w-3.5'
                  )}
                />
              )}
              <span className="flex-1 text-left">{item.label}</span>
            </div>
          )}
          {item.children && item.children.length > 0 && (
            <div
              className={cn(
                isCollapsed ? 'mt-3 flex flex-col items-center gap-3' : 'mt-1 space-y-1'
              )}
            >
              {item.children.map(child => renderNavItem(child, true))}
            </div>
          )}
        </div>
      )
    }

    const highlightCollapsed = isCollapsed && (item.isNew || item.isBeta)

    const buttonClasses = cn(
      'relative transition-all duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-200',
      isCollapsed
        ? 'relative mx-auto flex h-11 w-11 items-center justify-center rounded-xl text-slate-600 hover:bg-indigo-50 hover:text-indigo-700'
        : 'w-full flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-md text-slate-700 hover:bg-indigo-50 hover:text-indigo-700',
      active &&
        (isCollapsed
          ? 'bg-indigo-100 text-indigo-900 shadow-sm ring-1 ring-indigo-200'
          : 'bg-indigo-100/80 text-indigo-900 border-l-4 border-indigo-600 shadow-sm'),
      highlightCollapsed && !active && 'border border-indigo-100/70 text-slate-600',
      isDisabled &&
        (isCollapsed
          ? 'cursor-not-allowed opacity-70 hover:bg-transparent hover:text-slate-500'
          : 'cursor-not-allowed opacity-70 hover:bg-transparent hover:text-slate-500')
    )

    const iconClasses = cn(
      'h-4 w-4 shrink-0',
      active && isCollapsed && 'text-indigo-700',
      isDisabled && !isCollapsed && 'text-slate-500'
    )

    const handleButtonClick = () => {
      if (isDisabled || !item.path) return
      handleNavigate(item.path)
    }

    const buttonContent = (
      <button
        type="button"
        onClick={handleButtonClick}
        aria-disabled={isDisabled}
        disabled={isDisabled}
        aria-label={item.label}
        className={buttonClasses}
      >
        <div
          className={cn(
            'relative flex items-center gap-2',
            isCollapsed ? 'justify-center' : 'w-full'
          )}
        >
          {item.icon && <item.icon className={iconClasses} />}
          {!isCollapsed && (
            <span className="flex-1 whitespace-pre-line break-words text-left leading-tight">
              {item.label}
            </span>
          )}
          {!isCollapsed && item.isBeta && (
            <TooltipProvider delayDuration={120}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Badge
                    variant="outline"
                    className="ml-2 border-indigo-400/70 text-indigo-500 text-[10px] px-1 py-0 rounded-sm"
                  >
                    {t('common.beta')}
                  </Badge>
                </TooltipTrigger>
                <TooltipContent
                  side="right"
                  className="max-w-xs bg-gray-800 text-white text-xs px-3 py-1.5"
                >
                  {t(
                    'serviceDetail.beta_notice',
                    'ベータ版のため、UI および仕様、課金体系が変わることがあります'
                  )}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>
        {isCollapsed && item.isBeta && (
          <span
            aria-hidden
            className="pointer-events-none absolute -bottom-0.5 -right-0.5 flex h-4 min-w-[14px] items-center justify-center rounded-sm border border-indigo-200 bg-white text-[9px] font-semibold uppercase tracking-wider text-indigo-500 shadow-sm"
          >
            β
          </span>
        )}
        {isCollapsed && statusLabel && (
          <span
            aria-hidden
            className="pointer-events-none absolute -top-0.5 -left-0.5 flex h-4 min-w-[14px] items-center justify-center rounded-sm border border-amber-200 bg-white text-[9px] font-semibold tracking-wider text-amber-600 shadow-sm"
          >
            !
          </span>
        )}
        {isDisabled && !isCollapsed && statusLabel && (
          <span
            aria-hidden
            className="pointer-events-none absolute inset-0 rounded-md border border-amber-200/80 bg-white/85 backdrop-blur-[1px] flex items-center justify-center text-xs font-semibold text-amber-600 shadow-inner"
          >
            {statusLabel}
          </span>
        )}
      </button>
    )

    return (
      <div key={item.id}>
        {/* リーフ項目（例: 設定）の前にも区切り線を出せるようにする。 */}
        {!isCollapsed && item.showDividerBefore && (
          <div className="mx-3 mb-3 h-px rounded-full bg-gradient-to-r from-transparent via-indigo-100 to-transparent" />
        )}
        {isCollapsed && item.showDividerBefore && (
          <div className="px-2 pb-3">
            <div className="h-px w-full rounded-full bg-gradient-to-r from-transparent via-indigo-100 to-transparent" />
          </div>
        )}
        <div
          className={cn(
            isChild && !isCollapsed && 'ml-4',
            isCollapsed && 'w-full flex justify-center'
          )}
        >
          {isCollapsed ? (
            <Tooltip delayDuration={120}>
              <TooltipTrigger asChild>
                <span className="w-full flex justify-center">{buttonContent}</span>
              </TooltipTrigger>
              <TooltipContent side="right" className="text-xs font-medium text-slate-600">
                <div className="flex flex-col text-left">
                  <span>{item.label}</span>
                  {statusLabel && (
                    <span className="text-[11px] font-medium text-amber-600">{statusLabel}</span>
                  )}
                </div>
              </TooltipContent>
            </Tooltip>
          ) : (
            buttonContent
          )}
        </div>
      </div>
    )
  }

  return (
    <TooltipProvider delayDuration={120}>
      <div className="flex flex-col h-full" data-testid="sidebar-nav">
        <div
          className={cn(
            'border-b',
            isCollapsed ? 'px-0 py-4 flex flex-col items-center gap-3' : 'p-3'
          )}
        >
          {/* Top-left logo */}
          <button
            type="button"
            onClick={() => navigate('/')}
            aria-label={t('common.home', 'Home')}
            className={cn(
              'flex items-center transition-opacity hover:opacity-80',
              isCollapsed ? 'justify-center w-full' : ''
            )}
          >
            <img
              src="/logo.png"
              alt="AI Hack"
              className={cn('w-auto', isCollapsed ? 'h-8' : 'h-8 3xl:h-10')}
            />
            {!isCollapsed && <span className="ml-2 text-sm font-semibold">AI Hack</span>}
          </button>

          {(resolvedServiceId || isSettingsPage) && (
            <button
              type="button"
              onClick={handleBackToSpace}
              aria-label={t('common.backToSpace', 'Spaceに戻る')}
              className={cn(
                'flex items-center gap-2 text-sm font-medium text-muted-foreground transition-colors hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-200',
                isCollapsed
                  ? 'h-11 w-11 justify-center rounded-xl bg-indigo-50/60 text-indigo-600 hover:bg-indigo-100'
                  : ''
              )}
            >
              <ArrowLeft className="h-4 w-4" />
              {!isCollapsed && <span>{t('common.backToSpace', 'Back to Space')}</span>}
            </button>
          )}
          {isCollapsed && (
            <div className="flex flex-col items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg border border-indigo-100 bg-white shadow-sm">
                {iconSrc ? (
                  <img
                    src={iconSrc}
                    alt={service?.serviceName ? `${service.serviceName} favicon` : primaryDomain}
                    className="h-8 w-8 rounded-md object-cover"
                    onError={handleIconError}
                    loading="lazy"
                  />
                ) : (
                  <span className="text-xs font-semibold uppercase text-indigo-500">
                    {service?.serviceName?.[0] || primaryDomain?.[0] || 'S'}
                  </span>
                )}
              </div>
              {onToggleSidebar && (
                <Button
                  type="button"
                  size="icon"
                  variant="ghost"
                  onClick={onToggleSidebar}
                  aria-label={t('sidebar.openSidebar', 'サイドバーを開く')}
                  className="h-9 w-9 rounded-full border border-indigo-200 bg-white text-indigo-500 shadow-md transition-colors hover:bg-indigo-50 hover:text-indigo-600"
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              )}
            </div>
          )}
        </div>
        {/* Service Header */}
        {!isCollapsed && service && (
          <div className="relative border-b px-4 pb-4 pt-5">
            {onToggleSidebar && (
              <Button
                type="button"
                size="icon"
                variant="ghost"
                onClick={onToggleSidebar}
                aria-label={t('sidebar.closeSidebar', 'サイドバーを閉じる')}
                className="absolute top-1/2 right-0 -translate-y-1/2 translate-x-1/2 h-9 w-9 rounded-full border border-indigo-200 bg-white text-indigo-500 shadow-md transition-colors hover:bg-indigo-50 hover:text-indigo-600"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
            )}
            <div className="flex items-center gap-3 pr-10">
              {iconSrc ? (
                <img
                  src={iconSrc}
                  alt={service.serviceName ? `${service.serviceName} favicon` : primaryDomain}
                  className="h-10 w-10 rounded-lg border border-indigo-100 bg-white object-cover shadow-sm"
                  onError={handleIconError}
                  loading="lazy"
                />
              ) : (
                <div className="flex h-10 w-10 items-center justify-center rounded-lg border border-dashed border-indigo-200 bg-white text-xs font-semibold uppercase text-indigo-500">
                  {service.serviceName?.[0] || primaryDomain?.[0] || 'S'}
                </div>
              )}
              <div className="min-w-0">
                <h3 className="font-semibold text-sm truncate">{service.serviceName}</h3>
                <p className="text-xs text-muted-foreground truncate">{primaryDomain}</p>
              </div>
            </div>
          </div>
        )}

        {/* Navigation Items */}
        <nav
          className={cn(
            'flex-1 overflow-y-auto',
            isCollapsed ? 'py-4 flex flex-col items-center gap-3' : 'p-2 space-y-1'
          )}
        >
          {navItems.map(item => renderNavItem(item))}
        </nav>

        {/* Usage Status Bar (hidden on settings page) */}
        {!isSettingsPage && <UsageStatusBar spaceId={resolvedSpaceId} isCollapsed={isCollapsed} />}

        {/* User Section at bottom */}
        <div className={cn('border-t', isCollapsed ? 'px-2 py-3' : 'px-3 py-3')}>
          {user ? (
            <Popover>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  aria-haspopup="menu"
                  aria-expanded="false"
                  className={cn(
                    'w-full transition-colors',
                    isCollapsed
                      ? 'h-11 w-11 mx-auto flex items-center justify-center rounded-xl bg-indigo-50/60 text-indigo-600 hover:bg-indigo-100'
                      : 'flex items-center gap-2 px-2 py-2 rounded-md hover:bg-indigo-50'
                  )}
                >
                  <div className="h-8 w-8 rounded-full bg-[#54C09C]/10 flex items-center justify-center">
                    <UserIcon className="h-4 w-4 text-[#54C09C]" />
                  </div>
                  {!isCollapsed && (
                    <span className="text-sm font-medium truncate">{user.fullName}</span>
                  )}
                </button>
              </PopoverTrigger>
              <PopoverContent side="top" align="center" className="w-64 p-2">
                <div className="flex items-center gap-2 px-2 py-2">
                  <div className="h-9 w-9 rounded-full bg-[#54C09C]/10 flex items-center justify-center">
                    <UserIcon className="h-4 w-4 text-[#54C09C]" />
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm font-medium truncate">{user.fullName}</div>
                  </div>
                </div>
                <div className="my-2 h-px bg-gray-200" />
                <div className="flex flex-col gap-1">
                  <Button
                    variant="ghost"
                    className="justify-start gap-2"
                    onClick={() => navigate('/settings')}
                  >
                    <SettingsIcon className="h-4 w-4" />
                    <span className="text-sm">{t('common.userSettings')}</span>
                  </Button>
                  {/* Link to language settings */}
                  {/* Language option removed from user menu as requested */}
                  <Button
                    variant="ghost"
                    className="justify-start gap-2 text-destructive hover:bg-destructive/10"
                    onClick={async () => {
                      try {
                        await signOut()
                      } catch (e) {
                        console.error('Failed to log out:', e)
                      }
                    }}
                  >
                    <LogOutIcon className="h-4 w-4" />
                    <span className="text-sm">{t('common.logout')}</span>
                  </Button>
                </div>
              </PopoverContent>
            </Popover>
          ) : (
            <Button
              variant="ghost"
              className={cn('w-full justify-center', isCollapsed && 'h-11 w-11 p-0 rounded-xl')}
              onClick={() => navigate('/login')}
            >
              {t('common.login')}
            </Button>
          )}
        </div>
      </div>
    </TooltipProvider>
  )
}

// Usage Status Bar Component
function UsageStatusBar({ spaceId, isCollapsed }: { spaceId?: string; isCollapsed: boolean }) {
  const { t } = useTranslation()

  const { data: analysisUsageData, isLoading: isAnalysisLoading } = useFeatureUsage(
    spaceId,
    'ANALYSIS'
  )
  // ハルシネーション検知の月間枠（FR-16-5・インジケーター②）。
  // 汎用の枠APIを featureType 違いで引くだけ＝表示側は分析と同じ部品を通す。
  // ⚠️ 枠の実体は暫定＝将来 Billing ドメインへ載せ替える。
  const { data: hallucinationUsageData, isLoading: isHallucinationLoading } = useFeatureUsage(
    spaceId,
    'HALLUCINATION_DETECTION'
  )

  const usedLabel = t('sidebar.usage.used', 'Used')
  const remainingLabel = t('sidebar.usage.remaining', 'Remaining')
  const limitLabel = t('sidebar.usage.limit', 'Limit')

  const sections = useMemo(() => {
    if (!spaceId) return []

    const result: UsageSection[] = []

    const analysisSection = mapUsageToSection('analysis', analysisUsageData, {
      label: t('sidebar.usage.analysis.label', "This month's analysis status"),
      shortLabel: t('sidebar.usage.analysis.short', 'AN'),
      style: SECTION_STYLES.analysis,
    })
    if (analysisSection) {
      result.push(analysisSection)
    }

    // 検知は分析の下。実行状況カード（プロンプト生成 → 分析 → 検知）と並び順を揃える
    // ＝同じ機能が2つのUIで違う位置に出ると、対応を取り直すことになるため。
    const hallucinationSection = mapUsageToSection('hallucination', hallucinationUsageData, {
      label: t('sidebar.usage.hallucination.label', "This month's detection status"),
      shortLabel: t('sidebar.usage.hallucination.short', 'HD'),
      style: SECTION_STYLES.hallucination,
    })
    if (hallucinationSection) {
      result.push(hallucinationSection)
    }

    return result
  }, [spaceId, analysisUsageData, hallucinationUsageData, t])

  const isLoading = (isAnalysisLoading || isHallucinationLoading) && !analysisUsageData

  if (!spaceId) {
    return null
  }

  if (sections.length === 0) {
    if (isLoading) {
      return (
        <div className="border-t border-gray-200 px-3 py-4">
          <div className="space-y-2">
            <div className="h-3 bg-gray-200 rounded animate-pulse" />
            <div className="h-2 bg-gray-200 rounded animate-pulse" />
          </div>
        </div>
      )
    }
    return null
  }

  const getStatusTextClass = (section: UsageSection) => {
    if (section.isUnlimited) return section.style.accentTextClass
    if (section.percentage <= 0) return 'text-red-600'
    if (section.percentage <= 20) return 'text-orange-700'
    return section.style.accentTextClass
  }

  const getProgressColor = (section: UsageSection) => {
    if (section.isUnlimited) return 'bg-slate-500'
    if (section.percentage <= 0) return 'bg-red-600'
    if (section.percentage <= 20) return 'bg-orange-700'
    if (section.percentage <= 40) return 'bg-yellow-700'
    return section.style.progressClass
  }

  const getTooltip = (section: UsageSection) => {
    if (section.isUnlimited) {
      return t('sidebar.usage.noLimit', 'No limit configured')
    }

    if (section.id === 'hallucination') {
      return t(
        'sidebar.usage.hallucination.tooltip',
        '{{usedLabel}} {{used}} · {{remainingLabel}} {{remaining}} / {{limitLabel}} {{limit}}',
        {
          usedLabel,
          used: section.used,
          remainingLabel,
          remaining: section.remaining ?? 0,
          limitLabel,
          limit: section.limit ?? 0,
        }
      )
    }

    if (section.id === 'analysis') {
      return t(
        'sidebar.usage.analysis.tooltip',
        '{{usedLabel}} {{used}} · {{remainingLabel}} {{remaining}} / {{limitLabel}} {{limit}}',
        {
          usedLabel,
          used: section.used,
          remainingLabel,
          remaining: section.remaining ?? 0,
          limitLabel,
          limit: section.limit ?? 0,
        }
      )
    }

    return t(
      'sidebar.usage.prompt.tooltip',
      '{{usedLabel}} {{used}} · {{remainingLabel}} {{remaining}} / {{limitLabel}} {{limit}}',
      {
        usedLabel,
        used: section.used,
        remainingLabel,
        remaining: section.remaining ?? 0,
        limitLabel,
        limit: section.limit ?? 0,
      }
    )
  }

  if (isCollapsed) {
    return (
      <div className="border-t border-gray-200 px-2 py-3 flex flex-col items-center gap-3">
        {sections.map(section => (
          <Tooltip key={section.id}>
            <TooltipTrigger asChild>
              <div className="flex flex-col items-center text-[10px]">
                <span
                  className={cn(
                    'font-semibold tracking-wide uppercase',
                    section.style.badgeTextClass
                  )}
                >
                  {section.shortLabel}
                </span>
                <span className={cn('text-sm', getStatusTextClass(section))}>
                  {section.isUnlimited
                    ? t('common.usage.unlimited', 'Unlimited')
                    : section.remaining}
                </span>
                {!section.isUnlimited && (
                  <>
                    <div className="h-px w-8 bg-gray-300 my-0.5" />
                    <span className="text-gray-500">
                      {limitLabel}: {section.limit}
                    </span>
                  </>
                )}
              </div>
            </TooltipTrigger>
            <TooltipContent side="right" className="text-xs font-medium text-slate-600">
              {getTooltip(section)}
            </TooltipContent>
          </Tooltip>
        ))}
      </div>
    )
  }

  return (
    <div className="border-t border-gray-200 pt-4 px-3 pb-3 space-y-4">
      {sections.map(section => (
        <div key={section.id} className="space-y-2">
          <div className="flex flex-col gap-1">
            <span className="text-xs text-gray-600">{section.label}</span>
            {/* 見出しは「残り」だけを強調する。上限・使用済みは下段にあり、
                両方に出すと同じ数字が2箇所に並んで読み手が対応を取り直すことになる。 */}
            <span className={cn('text-xs font-medium', getStatusTextClass(section))}>
              {section.isUnlimited
                ? t('sidebar.usage.noLimit', 'No limit configured')
                : `${remainingLabel}: ${section.remaining ?? 0}`}
            </span>
          </div>

          {section.isUnlimited ? (
            <p className="text-[10px] text-gray-500">
              {t('sidebar.usage.noLimit', 'No limit configured')}
            </p>
          ) : (
            <>
              <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                <div
                  className={cn(
                    getProgressColor(section),
                    'h-2 rounded-full transition-all duration-300'
                  )}
                  style={{ width: `${Math.min(section.percentage, 100)}%` }}
                />
              </div>
              {/* 下段は使用済みと上限のみ（残りは見出しにある＝各数字を1箇所だけに置く）。 */}
              <div className="flex justify-between text-[10px] text-gray-500">
                <span>
                  {usedLabel}: {section.used}
                </span>
                <span>
                  {limitLabel}: {section.limit ?? 0}
                </span>
              </div>
            </>
          )}

          {!section.isUnlimited && section.percentage <= 20 && section.percentage > 0 && (
            <p className="text-[10px] text-orange-700 font-medium">
              {t('sidebar.usage.warning', 'Running low on remaining calls')}
            </p>
          )}
          {!section.isUnlimited && section.percentage <= 0 && (
            <p className="text-[10px] text-red-600 font-medium">
              {t('sidebar.usage.depleted', 'Usage limit reached')}
            </p>
          )}
        </div>
      ))}
    </div>
  )
}

type UsageSection = {
  id: UsageSectionId
  label: string
  shortLabel: string
  used: number
  limit: number | null
  remaining: number | null
  isUnlimited: boolean
  percentage: number
  style: UsageSectionStyle
}

function mapUsageToSection(
  id: UsageSectionId,
  data: AnalysisUsageResponse | undefined,
  config: { label: string; shortLabel: string; style: UsageSectionStyle }
): UsageSection | null {
  if (!data) return null

  const used = Math.max(0, data.currentMonthlyUsage ?? 0)
  const rawLimit = data.monthlyLimit
  const limit = typeof rawLimit === 'number' ? Math.max(0, rawLimit) : null
  const isLimitActive = data.isLimitActive ?? false
  const isUnlimited = !isLimitActive || limit === null

  const rawRemaining =
    typeof data.remainingCalls === 'number'
      ? data.remainingCalls
      : limit !== null
        ? limit - used
        : null

  const remaining = typeof rawRemaining === 'number' ? Math.max(0, Math.floor(rawRemaining)) : null

  let percentage = 100
  if (!isUnlimited) {
    if (limit && limit > 0 && typeof remaining === 'number') {
      percentage = Math.max(0, Math.min((remaining / limit) * 100, 100))
    } else if (limit === 0) {
      percentage = 0
    }
  }

  return {
    id,
    label: config.label,
    shortLabel: config.shortLabel,
    used,
    limit,
    remaining,
    isUnlimited,
    percentage,
    style: config.style,
  }
}
